/**
 * React Query keys and API functions for vocabulary data.
 *
 * Vocabulary entries map short codes to playbook entities
 * (formations, concepts, modifiers) for the play call system.
 */

import type { VocabularyEntry, VocabularyCategory } from '@/db/Types'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'
import { handleResponse } from '@/api/utils/ResponseHandler'

// ============================================================================
// TYPES
// ============================================================================

export interface CreateVocabularyInput {
	code: string
	category: VocabularyCategory
	entity_type: string
	entity_id: string
	display_name?: string | null
	auto_generated?: boolean
}

export interface UpdateVocabularyInput {
	code?: string
	category?: VocabularyCategory
	display_name?: string | null
	auto_generated?: boolean
}

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

export const vocabularyKeys = {
	all: ['vocabulary'] as const,
	byPlaybook: (playbookId: string) => ['vocabulary', playbookId] as const,
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export async function fetchVocabulary(playbookId: string): Promise<VocabularyEntry[]> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/vocabulary`)
	const data = await handleResponse<{ vocabulary: VocabularyEntry[] }>(response)
	return data.vocabulary ?? []
}

export async function createVocabularyEntry(
	playbookId: string,
	data: CreateVocabularyInput,
): Promise<VocabularyEntry> {
	const response = await secureJsonFetch(`/api/playbooks/${playbookId}/vocabulary`, {
		method: 'POST',
		body: data,
	})
	const result = await handleResponse<{ entry: VocabularyEntry }>(response)
	return result.entry
}

export async function updateVocabularyEntry(
	playbookId: string,
	id: string,
	data: UpdateVocabularyInput,
): Promise<VocabularyEntry> {
	const response = await secureJsonFetch(`/api/playbooks/${playbookId}/vocabulary/${id}`, {
		method: 'PUT',
		body: data,
	})
	const result = await handleResponse<{ entry: VocabularyEntry }>(response)
	return result.entry
}

export async function deleteVocabularyEntry(
	playbookId: string,
	id: string,
): Promise<void> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/vocabulary/${id}`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to delete vocabulary entry: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}
